'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Button, Dialog } from 'src/components';
import {
  useGetListsQuery,
  useDeleteListMutation,
} from 'src/api/redux/lists/listsApi';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import useLists from 'src/hooks/lists/useLists';
import { useRouter } from 'next/navigation';

/**
 * DeleteListDialog Component
 *
 * A dialog component that handles the deletion of lists with confirmation.
 * Implements error handling, loading states, and user feedback through notifications.
 *
 * @component
 * @example
 * ```tsx
 * <DeleteListDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   listId={123}
 * />
 * ```
 */
interface DeleteListDialogProps {
  /** Controls the visibility of the dialog */
  open: boolean;
  /** Callback function called when the dialog should close */
  onClose: () => void;
  /** ID of the list to be deleted */
  listId: number;
}

const DeleteListDialog = ({
  open,
  onClose,
  listId,
}: DeleteListDialogProps): React.ReactElement => {
  const { data: providerLists } = useGetListsQuery({});
  const [deleteList, { isLoading }] = useDeleteListMutation();
  const list = providerLists?.results?.find(({ id }) => id === listId);
  const log = useLogging();
  const { setNotification } = useNotification();
  const { selectedList, setSelectedList } = useLists();
  const router = useRouter();

  const handleDelete = async () => {
    try {
      await deleteList({ listId: listId.toString() }).unwrap();
      setNotification({
        title: 'List deleted successfully',
        message: 'List deleted successfully',
        type: 'success',
      });
      log.event('List deleted', {
        id: listId,
        name: list?.name || 'Unknown',
      });
      // If the deleted list was the selected one, clear the selection and navigate back
      if (selectedList?.id === listId.toString()) {
        setSelectedList(null);
        router.replace('/lists/my');
      }
      onClose();
    } catch (err) {
      log.exception('Error deleting list', err);
      setNotification({
        title: 'Error deleting list',
        message: err?.data?.error_message || err?.data?.details || 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  return (
    <Dialog isOpen={open} onClose={onClose} aria-labelledby="delete-list-dialog-title" data-testid="delete-list-dialog">
      <Dialog.Header>
        <div className="flex items-center justify-between ">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50">
              <Icon icon={faTrash} className="text-red-500" />
            </div>
            <div className="text-lg font-medium" id="delete-list-dialog-title">
              Delete List
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 transition-colors"
            aria-label="Close dialog"
            data-testid="delete-list-dialog-close"
          >
            <Icon icon={faXmark} />
          </button>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div className="text-sm text-neutral-700 p-4" role="alert" data-testid="delete-list-dialog-message">
          Are you sure you want to delete the list <strong>{list?.name}</strong>
          ? This is a permanent action and cannot be undone.
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant="outlined"
          onClick={onClose}
          aria-label="Cancel list deletion"
          className="w-full rounded-full"
          data-testid="delete-list-dialog-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          color="danger"
          onClick={handleDelete}
          loading={isLoading}
          aria-label="Confirm list deletion"
          aria-busy={isLoading}
          className="w-full rounded-full"
          data-testid="delete-list-dialog-confirm"
        >
          Delete
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { DeleteListDialog as default };
